'use client';

import { useState } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselControls,
  CarouselDots,
  type CarouselApi,
} from '@/app/_components/core/carousel';
import { getItemTrackId } from '@/app/_components/shared/utils/component-helpers';
import { useLabel } from '@/app/_contexts/labels-context';
import { CardItem } from './card-item';
import {
  getCarouselAlign,
  getCarouselItemClass,
  getCarouselContentClass,
} from './card-section-helpers';
import type {
  CardSectionComponent,
  CardSectionElement,
} from './card-section.types';

interface CardSectionCarouselProps {
  cards: CardSectionElement[];
  cardType: CardSectionComponent['cardType'];
  canScroll: boolean;
  showDots: boolean;
  trackingPrefix?: string;
}

// Mobile card carousel. Extracted into its own module so embla is only loaded
// (via the dynamic import in card-section) when a carousel card type is actually
// shown on a small viewport — grid / non-carousel card types ship no embla.
export function CardSectionCarousel({
  cards,
  cardType,
  canScroll,
  showDots,
  trackingPrefix,
}: CardSectionCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const carouselLabel = useLabel('carousel.cardSection', 'Card section');

  return (
    <Carousel
      setApi={setApi}
      className="w-full md:hidden"
      opts={{
        align: getCarouselAlign(cardType),
        loop: false,
        watchDrag: canScroll,
      }}
      ariaLabel={carouselLabel}
    >
      <CarouselContent className={getCarouselContentClass(cardType)}>
        {cards.map((card, index) => (
          <CarouselItem
            key={card.id || index}
            className={getCarouselItemClass(cardType)}
          >
            <CardItem
              card={card}
              cardType={cardType}
              trackId={
                trackingPrefix
                  ? cards.length > 1
                    ? `${trackingPrefix}-card-${getItemTrackId(card.id, index)}`
                    : `${trackingPrefix}-card`
                  : undefined
              }
            />
          </CarouselItem>
        ))}
      </CarouselContent>
      {showDots && (
        <CarouselControls className="mt-10 lg:mt-12">
          <CarouselDots api={api} />
        </CarouselControls>
      )}
    </Carousel>
  );
}
